import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Header } from "../components/app/Header";
import { BackButton } from "../components/app/BackButton";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { Check, X, Loader2, Sparkles, Crown, Tag } from "lucide-react";

const FeatureLine = ({ included, children, dim }) => (
  <li className="flex items-start gap-2.5 text-sm">
    {included ? (
      <Check className="h-4 w-4 text-bull shrink-0 mt-0.5" strokeWidth={2} />
    ) : (
      <X className="h-4 w-4 text-neutral-700 shrink-0 mt-0.5" strokeWidth={2} />
    )}
    <span className={included ? "text-neutral-200" : "text-neutral-600 line-through"}>{children}</span>
    {dim && <span className="ml-1 text-[10px] font-mono text-neutral-600">{dim}</span>}
  </li>
);

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const [loading, setLoading] = useState(null);
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponBusy, setCouponBusy] = useState(false);
  const [couponError, setCouponError] = useState("");

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    if (!user) { navigate("/login?mode=signup"); return; }
    setCouponBusy(true); setCouponError("");
    try {
      const { data } = await api.post("/stripe/validate-coupon", { coupon: couponInput.trim() });
      if (data.valid) {
        setAppliedCoupon(data);
        setCouponError("");
      } else {
        setAppliedCoupon(null);
        setCouponError(t("coupon_invalid"));
      }
    } catch {
      setAppliedCoupon(null);
      setCouponError(t("coupon_invalid"));
    } finally {
      setCouponBusy(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput("");
    setCouponError("");
  };

  const checkout = async (plan) => {
    if (!user) { navigate("/login?mode=signup"); return; }
    setLoading(plan);
    try {
      const origin = window.location.origin;
      const body = { plan, origin };
      if (appliedCoupon?.code) body.coupon = appliedCoupon.code;
      const { data } = await api.post("/stripe/create-checkout-session", body);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Stripe error");
      setLoading(null);
    }
  };

  const currentPlan = user?.role === "OWNER" ? "OWNER" : (user?.plan || "FREE");

  return (
    <div className="min-h-screen" data-testid="pricing-page">
      <Header />
      <main className="mx-auto max-w-7xl px-6 md:px-8 py-8 md:py-12">
        <BackButton fallback="/" />
        <div className="mt-6 text-center max-w-2xl mx-auto">
          <span className="label-mono">{t("nav_pricing").toUpperCase()}</span>
          <h1 className="mt-3 text-4xl sm:text-5xl font-semibold tracking-tighter">{t("pricing_title")}</h1>
          <p className="mt-4 text-neutral-400">{t("pricing_subtitle")}</p>
        </div>

        {/* Coupon input */}
        <div className="mt-10 max-w-md mx-auto" data-testid="coupon-section">
          {!appliedCoupon ? (
            <div className="surface-card p-4">
              <div className="flex items-center gap-2 mb-2">
                <Tag className="h-3.5 w-3.5 text-bull" strokeWidth={1.5} />
                <span className="label-mono">{t("coupon_label")}</span>
              </div>
              <div className="flex gap-2">
                <Input
                  value={couponInput}
                  onChange={(e) => { setCouponInput(e.target.value.toUpperCase()); setCouponError(""); }}
                  onKeyDown={(e) => { if (e.key === "Enter") applyCoupon(); }}
                  placeholder={t("coupon_placeholder")}
                  className="bg-[#0e0e0e] border-white/10 rounded-none h-10 font-mono focus-visible:ring-bull/40 focus-visible:border-bull/40 uppercase tracking-wider"
                  data-testid="coupon-input"
                />
                <Button
                  onClick={applyCoupon}
                  disabled={couponBusy || !couponInput.trim()}
                  className="rounded-none bg-bull text-black hover:bg-bull/90 h-10 px-5 font-medium"
                  data-testid="coupon-apply-btn"
                >
                  {couponBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("coupon_apply")}
                </Button>
              </div>
              {couponError && (
                <div className="mt-2 flex items-center gap-1.5 text-bear font-mono text-xs" data-testid="coupon-error">
                  <X className="h-3.5 w-3.5" strokeWidth={2} /> {couponError}
                </div>
              )}
            </div>
          ) : (
            <div className="surface-card p-4 border-bull/40 bg-bull/[0.04]" data-testid="coupon-applied">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <Check className="h-4 w-4 text-bull shrink-0" strokeWidth={2} />
                  <span className="font-mono text-sm text-bull truncate">
                    <strong>{appliedCoupon.code}</strong> {t("coupon_applied")} — {appliedCoupon.discount} · {appliedCoupon.duration}
                  </span>
                </div>
                <button
                  onClick={removeCoupon}
                  className="font-mono text-[11px] text-neutral-400 hover:text-bear tracking-wider"
                  data-testid="coupon-remove-btn"
                >
                  {t("coupon_remove")}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-px md:bg-white/10 md:border md:border-white/10 max-w-5xl mx-auto">
          {/* FREE */}
          <div className="bg-[#0a0a0a] p-8 flex flex-col" data-testid="plan-free-card">
            <div className="label-mono">{t("plan_FREE")}</div>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="font-mono text-5xl font-medium">$0</span>
            </div>
            <p className="mt-2 text-sm text-neutral-500">{t("pricing_free_label")}</p>
            <ul className="mt-8 space-y-3 flex-1">
              <FeatureLine included>{t("pricing_analyses_5")}</FeatureLine>
              <FeatureLine included>{t("pricing_feat_langs")}</FeatureLine>
              <FeatureLine included>{t("pricing_feat_styles")}</FeatureLine>
              <FeatureLine included={false}>{t("pricing_feat_export")}</FeatureLine>
              <FeatureLine included={false}>{t("pricing_feat_history")}</FeatureLine>
            </ul>
            <Button
              className="mt-8 w-full rounded-none bg-transparent border border-white/15 text-white hover:bg-white/5 h-11"
              onClick={() => navigate(user ? "/dashboard" : "/login?mode=signup")}
              disabled={currentPlan === "FREE"}
              data-testid="plan-free-cta"
            >
              {currentPlan === "FREE" ? t("pricing_current_cta") : t("pricing_free_cta")}
            </Button>
          </div>

          {/* TRADER (highlighted) */}
          <div className="relative bg-[#0d2b1a] p-8 flex flex-col border-2 border-bull md:border-2" data-testid="plan-trader-card">
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-bull text-black font-mono text-[10px] tracking-[0.18em] px-3 py-1">
              ★ {t("pricing_most_popular")}
            </span>
            <div className="flex items-center justify-between">
              <span className="label-mono text-bull">{t("plan_TRADER")}</span>
              <Sparkles className="h-4 w-4 text-bull" strokeWidth={1.5} />
            </div>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="font-mono text-5xl font-medium">$19.99</span>
              <span className="font-mono text-sm text-neutral-400">{t("pricing_per_month")}</span>
            </div>
            <p className="mt-2 text-sm text-neutral-500">{t("pricing_analyses_100")}</p>
            <ul className="mt-8 space-y-3 flex-1">
              <FeatureLine included>{t("pricing_analyses_100")}</FeatureLine>
              <FeatureLine included>{t("pricing_feat_langs")}</FeatureLine>
              <FeatureLine included>{t("pricing_feat_styles")}</FeatureLine>
              <FeatureLine included>{t("pricing_feat_export")}</FeatureLine>
              <FeatureLine included>{t("pricing_feat_history")}</FeatureLine>
              <FeatureLine included>{t("pricing_feat_priority")}</FeatureLine>
            </ul>
            <Button
              className="mt-8 w-full rounded-none bg-bull text-black hover:bg-bull/90 h-11 font-medium"
              onClick={() => checkout("TRADER")}
              disabled={currentPlan === "TRADER" || loading === "TRADER" || currentPlan === "OWNER"}
              data-testid="plan-trader-cta"
            >
              {loading === "TRADER" ? <Loader2 className="h-4 w-4 animate-spin" /> :
                currentPlan === "TRADER" ? t("pricing_current_cta") : t("pricing_trader_cta")}
            </Button>
          </div>

          {/* PRO */}
          <div className="bg-[#0a0a0a] p-8 flex flex-col border-l border-white/5" data-testid="plan-pro-card">
            <div className="flex items-center justify-between">
              <span className="label-mono">{t("plan_PRO")}</span>
              <Crown className="h-4 w-4 text-warn" strokeWidth={1.5} />
            </div>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="font-mono text-5xl font-medium">$99.99</span>
              <span className="font-mono text-sm text-neutral-400">{t("pricing_per_month")}</span>
            </div>
            <p className="mt-2 text-sm text-neutral-500">{t("pricing_analyses_unlimited")}</p>
            <ul className="mt-8 space-y-3 flex-1">
              <FeatureLine included>{t("pricing_analyses_unlimited")}</FeatureLine>
              <FeatureLine included>{t("pricing_feat_langs")}</FeatureLine>
              <FeatureLine included>{t("pricing_feat_styles")}</FeatureLine>
              <FeatureLine included>{t("pricing_feat_export")}</FeatureLine>
              <FeatureLine included>{t("pricing_feat_history")}</FeatureLine>
              <FeatureLine included>{t("pricing_feat_priority")}</FeatureLine>
              <FeatureLine included dim="SOON">{t("pricing_feat_api")}</FeatureLine>
            </ul>
            <Button
              className="mt-8 w-full rounded-none bg-white text-black hover:bg-neutral-200 h-11 font-medium"
              onClick={() => checkout("PRO")}
              disabled={currentPlan === "PRO" || loading === "PRO" || currentPlan === "OWNER"}
              data-testid="plan-pro-cta"
            >
              {loading === "PRO" ? <Loader2 className="h-4 w-4 animate-spin" /> :
                currentPlan === "PRO" ? t("pricing_current_cta") : t("pricing_pro_cta")}
            </Button>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-24 max-w-3xl mx-auto">
          <span className="label-mono">{t("pricing_faq_title")}</span>
          <Accordion type="single" collapsible className="mt-6 border-t border-white/10">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <AccordionItem key={i} value={`pq${i}`} className="border-b border-white/10" data-testid={`pricing-faq-${i}`}>
                <AccordionTrigger className="text-left font-medium hover:no-underline hover:text-bull py-5">{t(`pricing_faq_q${i}`)}</AccordionTrigger>
                <AccordionContent className="text-neutral-400 leading-relaxed pb-5 pr-8">{t(`pricing_faq_a${i}`)}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <p className="mt-16 text-center font-mono text-[11px] text-neutral-500 tracking-wider">
          STRIPE SECURED · PCI-DSS LEVEL 1 · CANCEL ANYTIME
        </p>
      </main>
    </div>
  );
}
